import {snackbarAtom} from 'atoms/snackbar';
import {type JiraObject, type WorkstreamMetric, WorkstreamService} from 'client';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getService} from 'utilities';

type CancellableRequest = {
  cancel?: () => void;
};

type PickerTab = 'metrics' | 'outcomes';

type UseWorkstreamManageOutcomesPickerDataProps = {
  open: boolean;
  workstreamId: number;
  loadElevatableJiraOutcomes?: () => Promise<JiraObject[]>;
};

export const useWorkstreamManageOutcomesPickerData = ({
  open,
  workstreamId,
  loadElevatableJiraOutcomes,
}: UseWorkstreamManageOutcomesPickerDataProps) => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [linkableMetrics, setLinkableMetrics] = useState<WorkstreamMetric[]>([]);
  const [elevatableJiraOutcomes, setElevatableJiraOutcomes] = useState<JiraObject[]>([]);
  const [hasAttemptedLinkableMetricsLoad, setHasAttemptedLinkableMetricsLoad] = useState(false);
  const [hasAttemptedElevatableJiraOutcomesLoad, setHasAttemptedElevatableJiraOutcomesLoad] =
    useState(false);
  const [isLoadingLinkableMetrics, setIsLoadingLinkableMetrics] = useState(false);
  const [isLoadingElevatableJiraOutcomes, setIsLoadingElevatableJiraOutcomes] = useState(false);
  const [linkableMetricsError, setLinkableMetricsError] = useState(false);
  const [elevatableJiraOutcomesError, setElevatableJiraOutcomesError] = useState(false);
  const wasOpenRef = useRef(false);
  const requestSessionRef = useRef(0);
  const linkableMetricsRequestRef = useRef<CancellableRequest | null>(null);
  const elevatableJiraOutcomesRequestRef = useRef<CancellableRequest | null>(null);

  const cancelRequests = useCallback(() => {
    requestSessionRef.current += 1;
    linkableMetricsRequestRef.current?.cancel?.();
    elevatableJiraOutcomesRequestRef.current?.cancel?.();
    linkableMetricsRequestRef.current = null;
    elevatableJiraOutcomesRequestRef.current = null;
  }, []);

  const reset = useCallback(() => {
    setLinkableMetrics([]);
    setElevatableJiraOutcomes([]);
    setHasAttemptedLinkableMetricsLoad(false);
    setHasAttemptedElevatableJiraOutcomesLoad(false);
    setIsLoadingLinkableMetrics(false);
    setIsLoadingElevatableJiraOutcomes(false);
    setLinkableMetricsError(false);
    setElevatableJiraOutcomesError(false);
  }, []);

  const loadLinkableMetrics = useCallback(() => {
    if (isLoadingLinkableMetrics) return;

    const requestSession = requestSessionRef.current;
    const request = getService(
      WorkstreamService
    ).getLinkableMetricsApiV1WorkstreamIdMetricsLinkableGet(workstreamId, true);
    linkableMetricsRequestRef.current = request;
    setHasAttemptedLinkableMetricsLoad(true);
    setIsLoadingLinkableMetrics(true);
    setLinkableMetricsError(false);

    void request
      .then((nextMetrics) => {
        if (requestSessionRef.current !== requestSession) return;
        setLinkableMetrics(nextMetrics);
      })
      .catch(() => {
        if (requestSessionRef.current !== requestSession) return;
        setLinkableMetricsError(true);
        setSnackbarMessage({text: t('manage_outcomes_linkable_metrics_error')});
      })
      .finally(() => {
        if (requestSessionRef.current !== requestSession) return;
        if (linkableMetricsRequestRef.current === request) {
          linkableMetricsRequestRef.current = null;
        }
        setIsLoadingLinkableMetrics(false);
      });
  }, [isLoadingLinkableMetrics, setSnackbarMessage, workstreamId]);

  const loadElevatableJiraOutcomesRequest = useCallback(() => {
    if (!loadElevatableJiraOutcomes || isLoadingElevatableJiraOutcomes) return;

    const requestSession = requestSessionRef.current;
    const request = loadElevatableJiraOutcomes();
    elevatableJiraOutcomesRequestRef.current = request as CancellableRequest;
    setHasAttemptedElevatableJiraOutcomesLoad(true);
    setIsLoadingElevatableJiraOutcomes(true);
    setElevatableJiraOutcomesError(false);

    void request
      .then((nextJiraOutcomes) => {
        if (requestSessionRef.current !== requestSession) return;
        setElevatableJiraOutcomes(nextJiraOutcomes);
      })
      .catch(() => {
        if (requestSessionRef.current !== requestSession) return;
        setElevatableJiraOutcomesError(true);
        setSnackbarMessage({text: t('manage_outcomes_elevatable_jira_outcomes_error')});
      })
      .finally(() => {
        if (requestSessionRef.current !== requestSession) return;
        if (elevatableJiraOutcomesRequestRef.current === request) {
          elevatableJiraOutcomesRequestRef.current = null;
        }
        setIsLoadingElevatableJiraOutcomes(false);
      });
  }, [isLoadingElevatableJiraOutcomes, loadElevatableJiraOutcomes, setSnackbarMessage]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      cancelRequests();
      return;
    }
    if (wasOpenRef.current) return;

    wasOpenRef.current = true;
    reset();
  }, [cancelRequests, open, reset]);

  useEffect(() => cancelRequests, [cancelRequests]);

  useEffect(() => {
    if (!open || hasAttemptedLinkableMetricsLoad) return;

    loadLinkableMetrics();
  }, [hasAttemptedLinkableMetricsLoad, loadLinkableMetrics, open]);

  useEffect(() => {
    if (!open || !loadElevatableJiraOutcomes || hasAttemptedElevatableJiraOutcomesLoad) return;

    loadElevatableJiraOutcomesRequest();
  }, [
    hasAttemptedElevatableJiraOutcomesLoad,
    loadElevatableJiraOutcomes,
    loadElevatableJiraOutcomesRequest,
    open,
  ]);

  const retryFailedLoads = useCallback(
    (tab: PickerTab) => {
      if (linkableMetricsError) {
        loadLinkableMetrics();
      }
      if (tab === 'outcomes' && elevatableJiraOutcomesError) {
        loadElevatableJiraOutcomesRequest();
      }
    },
    [
      elevatableJiraOutcomesError,
      linkableMetricsError,
      loadElevatableJiraOutcomesRequest,
      loadLinkableMetrics,
    ]
  );

  return {
    elevatableJiraOutcomes,
    isLoadingElevatableJiraOutcomes,
    isLoadingLinkableMetrics,
    linkableMetrics,
    retryFailedLoads,
    setElevatableJiraOutcomes,
  };
};
